import { RanaMapProps } from "@/lib/rana-map/types/rana-map-props-types";

import { getIsPublishedVersion } from "../../../store/selectors";
import { usePublicationStore } from "../../../store/store";
import { PublicatonMapId } from "../../../types/publication";
import { PublicationNewCommentForm } from "../components/comment-form";
import { PublicationCommentThread } from "../components/thread";

import { usePublicationComment } from "./use-comment";
import { usePublicationCommentLayer } from "./use-comment-layer";

export function usePublicationMapComments(publicationId: string, mapId: PublicatonMapId) {
  const commentHook = usePublicationComment(publicationId, mapId);
  const {
    addCommentToMap,
    addCommentToMapViaCoords,
    isAddingComment,
    setIsAddingComment,
    setCreatingCommentAt,
    selectedCommentThread,
    addCommentToThread,
    editCommentMessage,
    removeCommentThread,
    resolveComment,
    removeCommentMessage,
    toggleCommentReaction,
    setSelectedCommentThreadId,
    creatingCommentAt,
    createCommentThread,
  } = commentHook;

  const commentLayer = usePublicationCommentLayer(commentHook);

  /**
   * Comment popup rendering configuration.
   *
   * These render functions are passed to RanaMapSelf, which handles:
   * - Converting map coordinates (lng/lat) to screen positions (x/y)
   * - Automatically updating popup positions when the map moves or zooms
   * - Displaying popups at the correct screen location above comment markers
   */
  const isPublished = usePublicationStore(getIsPublishedVersion);
  const commentPopupConfig: RanaMapProps["commentProps"] = {
    isAddingComment,
    setIsAddingComment,
    addComment: addCommentToMap,
    canComment: isPublished && !!mapId, // Only allow commenting if the map is published and has an ID
    selectedCommentThread,
    onCloseCommentThread: () => setSelectedCommentThreadId(null),
    creatingCommentAt,
    onCloseCreatingCommentAt: () => setCreatingCommentAt(null),
    renderCommentThread: (thread) => (
      <PublicationCommentThread
        selectedCommentThread={thread}
        addCommentToThread={addCommentToThread}
        editCommentMessage={editCommentMessage}
        removeCommentThread={removeCommentThread}
        resolveComment={resolveComment}
        removeCommentMessage={removeCommentMessage}
        toggleCommentReaction={toggleCommentReaction}
      />
    ),
    renderNewCommentForm: () => (
      <PublicationNewCommentForm
        creatingCommentAt={creatingCommentAt}
        createCommentThread={createCommentThread}
      />
    ),
  };

  return {
    commentLayer,
    commentPopupConfig,
    addCommentToMapViaCoords,
    setIsAddingComment,
    commentHook,
  };
}
